import {
  Badge,
  Button,
  Dropdown,
  Field,
  Label,
  Menu,
  MenuButton,
  MenuGroup,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Option,
  Tab,
  TabList,
  TabValue,
  Tooltip,
  makeStyles,
  shorthands,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { useState, useEffect, useMemo } from "react";
import moment from "moment";
import { AppDispatch, RootState } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedParties,
  setAppliedSelectedDates,
} from "../../../redux/Commission/distributionSlice";
import { dateOptions } from "../../../shared/distributionDateOptions";
import SearchableDropdown from "../../../components/SearcbaleDropdown/SearchableDropdown";
import DynamicDatePicker from "../../../components/ControlledDatePicker/dynamicDatepicker";
import { getDateFilter } from "../../../utils";

const useStyles = makeStyles({
  menuPopover: {
    minWidth: "500px",
    ...shorthands.padding("12px"),
  },
  tabPanel: {
    ...shorthands.padding("15px"),
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  datePickers: {
    display: "flex",
    ...shorthands.gap("8px"),
    marginTop: "8px",
  },
  applyButton: {
    marginTop: "16px",
    width: "100%",
  },
  badge: {
    marginLeft: "4px",
    verticalAlign: "text-top",
  },
  filterButton: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  listbox: {
    maxHeight: "125px",
    overflowY: "auto",
    ...shorthands.margin("0"), // optional
  },
});

interface FilterTabProps {
  title: string;
  partyOptions: { label: string; id: string; checked: boolean }[];
  selectedMenuItem: string;
  getReceiptlistData: () => void;
  filterPopup: boolean;
  setFilterPopup: (open: boolean) => void;
  dateOption?: any;
  setDateOption?: any;
}

const FilterTab = (props: FilterTabProps) => {
  const styles = useStyles();
  const dispatch: AppDispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState<TabValue>("date");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [localSelectedPartiesNames, setLocalSelectedPartiesNames] = useState<
    string[]
  >([]);
  const dropdownOptions = props?.partyOptions?.map((party) => ({
    label: party?.label,
    value: party?.id,
  }));

  const selectedPartyNames = useSelector(
    (state: RootState) => state.distribution.selectedPartyNames
  );
  const selectedPartyNameKeys = selectedPartyNames?.map(
    (provider: any) => provider.id
  );
  // No parties applied = no filter = treat as "every party selected" in the UI,
  // matching how an empty selection is already interpreted server-side (fetch is unfiltered).
  const resolvedPartyIds = useMemo(
    () =>
      selectedPartyNameKeys && selectedPartyNameKeys.length > 0
        ? selectedPartyNameKeys
        : (dropdownOptions ?? []).map((o) => o.value),
    [selectedPartyNameKeys, dropdownOptions],
  );

  const appliedSelectedDates = useSelector(
    (state: RootState) => state.distribution.appliedSelectedDates
  );

  useEffect(() => {
    if (isOpen && appliedSelectedDates) {
      const params = new URLSearchParams(appliedSelectedDates);
      const from = params.get("fromDate");
      const to = params.get("toDate");

      if (from) setStartDate(moment(from).toDate());
      if (to) setEndDate(moment(to).toDate());
    }
  }, [isOpen, appliedSelectedDates]);

  const handleApply = () => {
    const dateFilter = getDateFilter(props?.dateOption, startDate, endDate);
    dispatch(setAppliedSelectedDates(dateFilter));
    // Every option checked is equivalent to no filter — store [] so the saved
    // preference doesn't freeze today's party list (and stays correct if new
    // parties are added later) and so re-applying an unchanged "all selected"
    // view never looks like an unsaved preference change.
    const allSelected =
      (dropdownOptions?.length ?? 0) > 0 &&
      localSelectedPartiesNames?.length === dropdownOptions?.length;
    const newParties: { id: string; name: string }[] = [];
    if (!allSelected) {
      dropdownOptions?.forEach((party) => {
        if (
          localSelectedPartiesNames?.includes(party.value) &&
          !newParties?.some((p) => p.id === party.value)
        ) {
          newParties.push({
            id: party.value,
            name: party.label,
          });
        }
      });
    }
    dispatch(setSelectedParties(newParties));
    props.getReceiptlistData();
    setIsOpen(false);
  };

  useEffect(() => {
    setIsOpen(props?.filterPopup);
    if (props?.filterPopup) {
      setLocalSelectedPartiesNames(resolvedPartyIds);
    }
  }, [props?.filterPopup]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync with parent state
  useEffect(() => {
    props?.setFilterPopup(isOpen);
  }, [isOpen]);
  const onFormatDate = (date?: Date): string => {
    return !date ? "" : moment(date).format("DD-MM-YYYY");
  };

  const getButtonContent = () => {
    return (
      <div className={styles.filterButton}>
        <span>{props?.selectedMenuItem}</span>
      </div>
    );
  };

  return (
    <Menu
      open={isOpen}
      onOpenChange={(_, { open }) => {
        setIsOpen(open);
        if (!open) {
          setLocalSelectedPartiesNames(resolvedPartyIds);
        }
      }}
      positioning="below-end"
    >
      <MenuTrigger>
        <MenuButton>{getButtonContent()}</MenuButton>
      </MenuTrigger>

      <MenuPopover className={styles.menuPopover}>
        <MenuList>
          <MenuGroup>
            <TabList
              selectedValue={selectedTab}
              onTabSelect={(_, { value }) => setSelectedTab(value)}
            >
              <Tab value="date">Select Date</Tab>
              <Tab value="parties" data-testid="select_parties_tab">
                Select Parties
              </Tab>
            </TabList>

            {selectedTab === "date" && (
              <div className={styles.tabPanel}>
                <Label>Select Date Range</Label>
                <Dropdown
                  placeholder={
                    props?.dateOption
                      ? dateOptions.find(
                        (opt) => opt.value === props.dateOption
                      )?.label
                      : "Select Date Range"
                  }
                  selectedOptions={[props?.dateOption]}
                  onOptionSelect={(_, data) =>
                    props?.setDateOption(data?.optionValue as string)
                  }
                  style={{ marginBottom: "12px" }}
                >
                  {dateOptions?.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Dropdown>

                {props?.dateOption === "custom" && (
                  <div className={styles.datePickers}>
                    <div style={{ flex: 1 }}>
                      <DynamicDatePicker
                        name="start_date"
                        label="Start Date"
                        required={false}
                        disabled={false}
                        formik={{
                          values: {
                            start_date: startDate ?? null,
                          },
                          setFieldValue: (
                            _name: string,
                            value: Date | null
                          ) => {
                            setStartDate(value);
                          },
                          setFieldTouched: () => { },
                          setFieldError: () => { },
                          errors: {},
                          touched: {},
                        }}
                      />
                    </div>

                    <div style={{ flex: 1 }}>
                      <DynamicDatePicker
                        name="end_date"
                        label="End Date"
                        required={false}
                        disabled={false}
                        formik={{
                          values: {
                            end_date: endDate ?? null,
                          },
                          setFieldValue: (
                            _name: string,
                            value: Date | null
                          ) => {
                            setEndDate(value);
                          },
                          setFieldTouched: () => { },
                          setFieldError: () => { },
                          errors: {},
                          touched: {},
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedTab === "parties" && (
              <div className={styles.tabPanel}>
                <label>Select Parties</label>
                <SearchableDropdown
                  select_all={true}
                  fixed_width={false}
                  style={styles.listbox}
                  options={dropdownOptions}
                  selectedDropDownValues={localSelectedPartiesNames}
                  setSelectedDropDownValues={setLocalSelectedPartiesNames}
                  placeholder="Select Parties"
                />
              </div>
            )}
            <div className="flex-end">
              <Button
                onClick={() => {
                  props?.setFilterPopup(false);
                  setLocalSelectedPartiesNames(resolvedPartyIds);
                }}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                className="asc-button-primary"
                onClick={handleApply}
                disabled={
                  props?.dateOption === "custom" && (!startDate || !endDate)
                }
              >
                Apply Filters
              </Button>
            </div>
          </MenuGroup>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

export default FilterTab;
